import React, { useState, useRef } from 'react';
import { FileText, BookOpen, Image as ImageIcon, Star, HelpCircle, Megaphone, ArrowLeft, Edit2, Eye, Save, Plus, Trash2, ChevronUp, ChevronDown, EyeOff, Copy, Upload, Search, Filter, CheckCircle2, XCircle, ExternalLink, Calendar, Tag, FileImage, FileVideo, File, X, Percent, Clock, Pin } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { ImageUpload } from '../ui/ImageUpload';
import { useToast } from '../ui/Toast';
import { useBlog, type CmsBlogPost } from '../../contexts/BlogContext';
type CmsView = 'dashboard' | 'pages' | 'page_editor' | 'blog' | 'blog_editor' | 'media' | 'reviews' | 'faq' | 'promotions';
interface PageSection {
  id: string;
  name: string;
  title: string;
  description: string;
  buttonText: string;
  imageUrl: string;
  visible: boolean;
}
interface SitePage {
  id: string;
  title: string;
  slug: string;
  status: string;
  lastModified: string;
  group: string;
}
interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  category: string;
  status: string;
  date: string;
  views: number;
  content: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string;
  imageUrl: string;
  tags: string[];
}
interface MediaItem {
  id: number;
  name: string;
  type: 'image' | 'video' | 'document';
  size: string;
  date: string;
  url: string;
  color: string;
}
interface ReviewItem {
  id: number;
  author: string;
  rating: number;
  text: string;
  date: string;
  status: 'published' | 'pending' | 'rejected';
  service: string;
}
interface FaqItem {
  id: number;
  category: string;
  question: string;
  answer: string;
}
interface Promotion {
  id: number;
  name: string;
  discount: string;
  startDate: string;
  endDate: string;
  status: 'active' | 'scheduled' | 'expired';
  usageCount: number;
  code: string;
}
interface CmsManagerProps {
  onNavigate: (page: string) => void;
}
export function CmsManager({
  onNavigate
}: CmsManagerProps) {
  const {
    success
  } = useToast();
  const {
    cmsPosts,
    setCmsPosts
  } = useBlog();
  const [currentView, setCurrentView] = useState<CmsView>('dashboard');
  // --- PAGES STATE ---
  const [selectedPage, setSelectedPage] = useState<SitePage | null>(null);
  const [pagesFilter, setPagesFilter] = useState<string>('all');
  const [pages] = useState<SitePage[]>([
  // Основные
  {
    id: 'home',
    title: 'Главная',
    slug: '/',
    status: 'published',
    lastModified: '15 Мар 2026',
    group: 'Основные'
  }, {
    id: 'about',
    title: 'О нас',
    slug: '/about',
    status: 'published',
    lastModified: '10 Мар 2026',
    group: 'Основные'
  }, {
    id: 'contact',
    title: 'Контакты',
    slug: '/contact',
    status: 'published',
    lastModified: '12 Мар 2026',
    group: 'Основные'
  }, {
    id: 'pricing',
    title: 'Цены',
    slug: '/pricing',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Основные'
  }, {
    id: 'faq',
    title: 'FAQ',
    slug: '/faq',
    status: 'published',
    lastModified: '08 Мар 2026',
    group: 'Основные'
  }, {
    id: 'blog',
    title: 'Блог',
    slug: '/blog',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Основные'
  }, {
    id: 'free-chart',
    title: 'Бесплатная карта',
    slug: '/free-chart',
    status: 'published',
    lastModified: '05 Мар 2026',
    group: 'Основные'
  }, {
    id: 'privacy',
    title: 'Политика конфиденциальности',
    slug: '/privacy',
    status: 'published',
    lastModified: '01 Мар 2026',
    group: 'Основные'
  }, {
    id: 'terms',
    title: 'Условия использования',
    slug: '/terms',
    status: 'published',
    lastModified: '01 Мар 2026',
    group: 'Основные'
  },
  // Услуги
  {
    id: 'service-natal',
    title: 'Натальная карта',
    slug: '/service-natal',
    status: 'published',
    lastModified: '13 Мар 2026',
    group: 'Услуги'
  }, {
    id: 'service-solar',
    title: 'Соляр',
    slug: '/service-solar',
    status: 'published',
    lastModified: '13 Мар 2026',
    group: 'Услуги'
  }, {
    id: 'service-compatibility',
    title: 'Совместимость',
    slug: '/service-compatibility',
    status: 'published',
    lastModified: '12 Мар 2026',
    group: 'Услуги'
  }, {
    id: 'service-combo',
    title: 'Комбо-анализ',
    slug: '/service-combo',
    status: 'published',
    lastModified: '12 Мар 2026',
    group: 'Услуги'
  }, {
    id: 'service-forecast',
    title: 'Прогноз транзитов',
    slug: '/service-forecast',
    status: 'published',
    lastModified: '11 Мар 2026',
    group: 'Услуги'
  }, {
    id: 'service-consultation',
    title: 'Консультация',
    slug: '/service-consultation',
    status: 'published',
    lastModified: '11 Мар 2026',
    group: 'Услуги'
  },
  // Ежедневник
  {
    id: 'astro-daily',
    title: 'Астро-ежедневник',
    slug: '/astro-daily',
    status: 'published',
    lastModified: '15 Мар 2026',
    group: 'Ежедневник'
  }, {
    id: 'daily-horoscope',
    title: 'Ежедневный гороскоп',
    slug: '/daily-horoscope',
    status: 'published',
    lastModified: '15 Мар 2026',
    group: 'Ежедневник'
  }, {
    id: 'moon-calendar',
    title: 'Лунный календарь',
    slug: '/moon-calendar',
    status: 'published',
    lastModified: '15 Мар 2026',
    group: 'Ежедневник'
  },
  // Каталог
  {
    id: 'astrologers',
    title: 'Каталог астрологов',
    slug: '/astrologers',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Каталог'
  }, {
    id: 'interpretations',
    title: 'Интерпретации',
    slug: '/interpretations',
    status: 'published',
    lastModified: '10 Мар 2026',
    group: 'Каталог'
  }, {
    id: 'calendar',
    title: 'Астро-календарь',
    slug: '/calendar',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Каталог'
  }, {
    id: 'marketplace',
    title: 'Маркетплейс услуг',
    slug: '/marketplace',
    status: 'published',
    lastModified: '09 Мар 2026',
    group: 'Каталог'
  },
  // Маркетинг
  {
    id: 'for-astrologers',
    title: 'Для астрологов',
    slug: '/for-astrologers',
    status: 'published',
    lastModified: '08 Мар 2026',
    group: 'Маркетинг'
  }, {
    id: 'advertising',
    title: 'Реклама на сайте',
    slug: '/advertising',
    status: 'published',
    lastModified: '05 Мар 2026',
    group: 'Маркетинг'
  },
  // Пользователи
  {
    id: 'login',
    title: 'Авторизация',
    slug: '/login',
    status: 'published',
    lastModified: '01 Мар 2026',
    group: 'Пользователи'
  }, {
    id: 'checkout',
    title: 'Оформление заказа',
    slug: '/checkout',
    status: 'published',
    lastModified: '01 Мар 2026',
    group: 'Пользователи'
  }, {
    id: 'dashboard-user',
    title: 'Личный кабинет',
    slug: '/dashboard-user',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Пользователи'
  }, {
    id: 'dashboard-astrologer',
    title: 'Кабинет астролога',
    slug: '/dashboard-astrologer',
    status: 'published',
    lastModified: '14 Мар 2026',
    group: 'Пользователи'
  }]);
  const [pageSections, setPageSections] = useState<Record<string, PageSection[]>>({
    home: [{
      id: 'hero',
      name: 'Hero баннер',
      title: 'Ваш путеводитель по звёздам',
      description: 'Профессиональные астрологические услуги: натальные карты, прогнозы, совместимость и консультации с опытными астрологами.',
      buttonText: 'Рассчитать карту',
      imageUrl: '',
      visible: true
    }, {
      id: 'services',
      name: 'Быстрые услуги',
      title: 'Наши услуги',
      description: 'Натальная карта, Соляр, Комбо: Натал + Соляр, Совместимость, Прогноз, Консультация — выберите подходящий формат.',
      buttonText: 'Все услуги',
      imageUrl: '',
      visible: true
    }, {
      id: 'advantages',
      name: 'Преимущества',
      title: 'Почему выбирают AstroGuide',
      description: 'Точные расчёты по швейцарским эфемеридам, профессиональные астрологи с верификацией, мгновенные авто-отчёты, удобный личный кабинет.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'packages',
      name: 'Пакеты отчётов',
      title: 'Выгодные пакеты',
      description: 'Комбинируйте услуги и экономьте до 30%. Пакет «Базовый», «Расширенный», «Полный».',
      buttonText: 'Смотреть пакеты',
      imageUrl: '',
      visible: true
    }, {
      id: 'reviews',
      name: 'Отзывы клиентов',
      title: 'Что говорят наши клиенты',
      description: 'Реальные отзывы пользователей о натальных картах, прогнозах и консультациях.',
      buttonText: 'Все отзывы',
      imageUrl: '',
      visible: true
    }, {
      id: 'cta',
      name: 'CTA блок',
      title: 'Готовы узнать свою судьбу?',
      description: 'Закажите персональный астрологический разбор прямо сейчас. Первый отчёт со скидкой 20%.',
      buttonText: 'Начать сейчас',
      imageUrl: '',
      visible: true
    }],
    about: [{
      id: 'hero',
      name: 'Hero',
      title: 'Делаем профессиональную астрологию доступной',
      description: 'AstroGuide — это платформа нового поколения, объединяющая точные алгоритмы расчета, глубокие знания профессиональных астрологов и современные технологии.',
      buttonText: 'Рассчитать карту',
      imageUrl: '',
      visible: true
    }, {
      id: 'mission',
      name: 'Миссия',
      title: 'Наша миссия',
      description: 'Сделать качественную астрологию доступной каждому. Мы верим, что понимание своей натальной карты помогает принимать осознанные решения.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'values',
      name: 'Ценности',
      title: 'Наши ценности',
      description: 'Точность расчётов, научный подход, верификация астрологов, прозрачность, защита данных, постоянное развитие.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'team',
      name: 'Команда',
      title: 'Наша команда',
      description: 'Более 50 верифицированных астрологов, команда разработчиков и аналитиков. Средний опыт астрологов — 12 лет.',
      buttonText: 'Наши астрологи',
      imageUrl: '',
      visible: true
    }, {
      id: 'stats',
      name: 'Статистика',
      title: 'AstroGuide в цифрах',
      description: '50 000+ пользователей, 120 000+ отчётов, 50+ астрологов, 4.8 средний рейтинг.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    contact: [{
      id: 'hero',
      name: 'Hero',
      title: 'Свяжитесь с нами',
      description: 'Мы всегда рады помочь. Выберите удобный способ связи или заполните форму обратной связи.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'form',
      name: 'Форма обратной связи',
      title: 'Написать нам',
      description: 'Заполните форму, и мы ответим в течение 24 часов. Поля: Имя, Email, Тема, Сообщение.',
      buttonText: 'Отправить',
      imageUrl: '',
      visible: true
    }, {
      id: 'contacts',
      name: 'Контактная информация',
      title: 'Контакты',
      description: 'Email: support@astroguide.ru | Telegram: @astroguide_support | Время работы: Пн-Пт 10:00-19:00 МСК',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'faq_link',
      name: 'Ссылка на FAQ',
      title: 'Часто задаваемые вопросы',
      description: 'Возможно, ответ на ваш вопрос уже есть в нашем разделе FAQ.',
      buttonText: 'Перейти в FAQ',
      imageUrl: '',
      visible: true
    }],
    pricing: [{
      id: 'hero',
      name: 'Hero',
      title: 'Тарифы и цены',
      description: 'Прозрачные цены на все астрологические услуги. Выберите подходящий формат: авто-отчёт или консультация с астрологом.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'auto_reports',
      name: 'Авто-отчёты',
      title: 'Автоматические отчёты',
      description: 'Натальная карта от 3 900 ₽, Соляр от 4 400 ₽, Совместимость от 4 900 ₽, Комбо от 6 900 ₽, Прогноз от 3 900 ₽.',
      buttonText: 'Выбрать отчёт',
      imageUrl: '',
      visible: true
    }, {
      id: 'consultations',
      name: 'Консультации',
      title: 'Консультации с астрологом',
      description: 'Видеоконсультация 60 мин от 2 990 ₽, Расширенная 90 мин от 4 490 ₽, VIP 120 мин от 6 990 ₽.',
      buttonText: 'Записаться',
      imageUrl: '',
      visible: true
    }, {
      id: 'packages',
      name: 'Пакеты',
      title: 'Выгодные пакеты',
      description: 'Базовый (Натал + Соляр) — 6 900 ₽, Расширенный (+Совместимость) — 10 900 ₽, Полный (все отчёты) — 15 900 ₽.',
      buttonText: 'Выбрать пакет',
      imageUrl: '',
      visible: true
    }, {
      id: 'comparison',
      name: 'Сравнение',
      title: 'Сравнение тарифов',
      description: 'Таблица сравнения: что входит в каждый тариф, формат доставки, сроки, гарантии.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'faq',
      name: 'FAQ по ценам',
      title: 'Вопросы о ценах',
      description: 'Есть ли рассрочка? Можно ли вернуть деньги? Какие способы оплаты? Есть ли скидки?',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    faq: [{
      id: 'hero',
      name: 'Hero',
      title: 'Часто задаваемые вопросы',
      description: 'Ответы на популярные вопросы о наших услугах, оплате, консультациях и технических моментах.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'categories',
      name: 'Категории',
      title: 'Разделы FAQ',
      description: 'Общие вопросы, Отчёты и услуги, Оплата и возврат, Консультации, Астрологи, Техподдержка.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'contact_cta',
      name: 'CTA поддержки',
      title: 'Не нашли ответ?',
      description: 'Напишите нам, и мы ответим в течение 24 часов.',
      buttonText: 'Написать в поддержку',
      imageUrl: '',
      visible: true
    }],
    blog: [{
      id: 'hero',
      name: 'Hero',
      title: 'Блог AstroGuide',
      description: 'Статьи об астрологии, лунные календари, прогнозы, обучающие материалы и советы от профессиональных астрологов.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'categories',
      name: 'Категории',
      title: 'Разделы блога',
      description: 'Астрология, Обучение, Лунный календарь, Совместимость, Советы, Практики, Новости.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'featured',
      name: 'Избранные статьи',
      title: 'Популярное',
      description: 'Блок с избранными и самыми читаемыми статьями.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'free-chart': [{
      id: 'hero',
      name: 'Hero',
      title: 'Бесплатная натальная карта',
      description: 'Рассчитайте свою натальную карту бесплатно. Введите дату, время и место рождения для мгновенного результата.',
      buttonText: 'Рассчитать бесплатно',
      imageUrl: '',
      visible: true
    }, {
      id: 'form',
      name: 'Форма расчёта',
      title: 'Данные для расчёта',
      description: 'Поля: Дата рождения, Время рождения, Город рождения. Точное время даёт более точный результат.',
      buttonText: 'Рассчитать',
      imageUrl: '',
      visible: true
    }, {
      id: 'upsell',
      name: 'Апсейл',
      title: 'Хотите полный разбор?',
      description: 'Бесплатная карта — это только начало. Закажите полный отчёт с детальными интерпретациями.',
      buttonText: 'Заказать полный отчёт',
      imageUrl: '',
      visible: true
    }],
    privacy: [{
      id: 'title',
      name: 'Заголовок',
      title: 'Политика конфиденциальности',
      description: 'Настоящая политика конфиденциальности описывает порядок обработки персональных данных пользователей сервиса AstroGuide.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'content',
      name: 'Содержание',
      title: 'Основные положения',
      description: 'Сбор данных, использование, хранение, передача третьим лицам, права пользователей, cookies, изменения политики.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    terms: [{
      id: 'title',
      name: 'Заголовок',
      title: 'Условия использования',
      description: 'Настоящие условия регулируют использование сервиса AstroGuide и предоставляемых услуг.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'content',
      name: 'Содержание',
      title: 'Основные положения',
      description: 'Регистрация, оплата, возврат, интеллектуальная собственность, ограничение ответственности, разрешение споров.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'service-natal': [{
      id: 'hero',
      name: 'Hero',
      title: 'Натальная карта',
      description: 'Глубокий анализ вашей личности, талантов, кармических задач и жизненного пути на основе точного расчёта натальной карты.',
      buttonText: 'Заказать разбор',
      imageUrl: '',
      visible: true
    }, {
      id: 'what_is',
      name: 'Что это',
      title: 'Что такое натальная карта?',
      description: 'Натальная карта — это карта неба в момент вашего рождения. Она показывает расположение планет по знакам и домам.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'includes',
      name: 'Что включено',
      title: 'Что входит в отчёт',
      description: 'Анализ Солнца, Луны, Асцендента, планет в знаках и домах, аспекты, кармические узлы, таланты, профессия, отношения.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы отчёта',
      description: 'Базовый (авто-отчёт PDF) от 3 900 ₽ — мгновенно. Премиум (ручной разбор астрологом) от 7 900 ₽ — 3-5 дней.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }, {
      id: 'reviews',
      name: 'Отзывы',
      title: 'Отзывы клиентов',
      description: 'Реальные отзывы пользователей, заказавших натальную карту.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'faq',
      name: 'FAQ',
      title: 'Частые вопросы',
      description: 'Нужно ли точное время рождения? Чем отличается от гороскопа? Как долго ждать?',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'service-solar': [{
      id: 'hero',
      name: 'Hero',
      title: 'Соляр — прогноз на год',
      description: 'Персональный прогноз на ваш личный год: ключевые события, возможности и вызовы с точностью до месяца.',
      buttonText: 'Заказать соляр',
      imageUrl: '',
      visible: true
    }, {
      id: 'what_is',
      name: 'Что это',
      title: 'Что такое соляр?',
      description: 'Соляр — это карта, построенная на момент точного возвращения Солнца в позицию при рождении. Показывает темы года.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'includes',
      name: 'Что включено',
      title: 'Что входит в отчёт',
      description: 'Общая тема года, помесячный прогноз, сферы активности, благоприятные периоды, рекомендации.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы отчёта',
      description: 'Базовый (авто-отчёт) от 4 400 ₽. Премиум (ручной разбор) от 8 900 ₽.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }, {
      id: 'faq',
      name: 'FAQ',
      title: 'Частые вопросы',
      description: 'Когда лучше заказывать соляр? Чем отличается от натальной карты?',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'service-compatibility': [{
      id: 'hero',
      name: 'Hero',
      title: 'Анализ совместимости',
      description: 'Детальный разбор отношений двух людей: синастрия, кармические связи, зоны роста и потенциал пары.',
      buttonText: 'Заказать анализ',
      imageUrl: '',
      visible: true
    }, {
      id: 'includes',
      name: 'Что включено',
      title: 'Что входит в отчёт',
      description: 'Синастрия (наложение карт), композит (карта отношений), кармические связи, рекомендации для пары.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы отчёта',
      description: 'Базовый от 4 900 ₽. Премиум от 9 900 ₽.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }, {
      id: 'faq',
      name: 'FAQ',
      title: 'Частые вопросы',
      description: 'Нужны ли данные обоих партнёров? Можно ли проверить совместимость с другом?',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'service-combo': [{
      id: 'hero',
      name: 'Hero',
      title: 'Комбо: Натал + Соляр',
      description: 'Полная картина: кто вы и что вас ждёт в этом году. Два отчёта по цене одного со скидкой.',
      buttonText: 'Заказать комбо',
      imageUrl: '',
      visible: true
    }, {
      id: 'includes',
      name: 'Что включено',
      title: 'Что входит',
      description: 'Полный натальный разбор + годовой прогноз (соляр). Экономия до 25% по сравнению с отдельными заказами.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы',
      description: 'Базовый комбо от 6 900 ₽. Премиум комбо от 14 900 ₽.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }],
    'service-forecast': [{
      id: 'hero',
      name: 'Hero',
      title: 'Прогноз транзитов',
      description: 'Анализ текущих и предстоящих транзитов планет относительно вашей натальной карты. Ключевые периоды и рекомендации.',
      buttonText: 'Заказать прогноз',
      imageUrl: '',
      visible: true
    }, {
      id: 'includes',
      name: 'Что включено',
      title: 'Что входит в отчёт',
      description: 'Транзиты медленных планет, ключевые даты, благоприятные периоды для бизнеса, отношений, здоровья.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы',
      description: 'Базовый (3 месяца) от 3 900 ₽. Расширенный (12 месяцев) от 6 900 ₽.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }],
    'service-consultation': [{
      id: 'hero',
      name: 'Hero',
      title: 'Консультация с астрологом',
      description: 'Живой разговор с профессиональным астрологом по видеосвязи. Ответы на ваши вопросы, персональные рекомендации.',
      buttonText: 'Записаться',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы',
      title: 'Форматы консультаций',
      description: 'Экспресс 30 мин от 1 990 ₽, Стандарт 60 мин от 2 990 ₽, Расширенная 90 мин от 4 490 ₽, VIP 120 мин от 6 990 ₽.',
      buttonText: 'Выбрать формат',
      imageUrl: '',
      visible: true
    }, {
      id: 'how_it_works',
      name: 'Как это работает',
      title: 'Как проходит консультация',
      description: '1. Выберите астролога → 2. Выберите время → 3. Оплатите → 4. Получите ссылку на видеозвонок → 5. Консультация.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'astrologers',
      name: 'Астрологи',
      title: 'Наши астрологи',
      description: 'Все астрологи проходят верификацию. Средний опыт — 12 лет. Рейтинг от 4.5 звёзд.',
      buttonText: 'Выбрать астролога',
      imageUrl: '',
      visible: true
    }],
    'astro-daily': [{
      id: 'hero',
      name: 'Hero',
      title: 'Астро-ежедневник',
      description: 'Ежедневные гороскопы, лунный календарь, астрологические советы дня и планетарные часы.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'horoscope',
      name: 'Гороскоп',
      title: 'Ежедневный гороскоп',
      description: 'Персональный гороскоп на сегодня для каждого знака зодиака с рекомендациями.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'moon',
      name: 'Луна сегодня',
      title: 'Лунный день',
      description: 'Текущий лунный день, фаза Луны, знак Луны, рекомендации.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'daily-horoscope': [{
      id: 'hero',
      name: 'Hero',
      title: 'Ежедневный гороскоп',
      description: 'Астрологический прогноз на сегодня для всех знаков зодиака.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'signs',
      name: 'Знаки зодиака',
      title: 'Выберите знак',
      description: 'Овен, Телец, Близнецы, Рак, Лев, Дева, Весы, Скорпион, Стрелец, Козерог, Водолей, Рыбы.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'moon-calendar': [{
      id: 'hero',
      name: 'Hero',
      title: 'Лунный календарь',
      description: 'Подробный лунный календарь с фазами Луны, лунными днями и рекомендациями на каждый день.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'calendar',
      name: 'Календарь',
      title: 'Календарь на месяц',
      description: 'Визуальный календарь с фазами Луны, благоприятными и неблагоприятными днями.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    astrologers: [{
      id: 'hero',
      name: 'Hero',
      title: 'Каталог астрологов',
      description: 'Найдите своего астролога. Все специалисты прошли верификацию и имеют подтверждённый опыт.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'filters',
      name: 'Фильтры',
      title: 'Фильтры поиска',
      description: 'Специализация, рейтинг, цена, опыт, язык консультации, доступность.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'catalog',
      name: 'Список астрологов',
      title: 'Наши астрологи',
      description: 'Карточки астрологов с фото, рейтингом, специализацией, ценой и кнопкой записи.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    interpretations: [{
      id: 'hero',
      name: 'Hero',
      title: 'Астрологические интерпретации',
      description: 'Энциклопедия астрологических значений: планеты в знаках, дома, аспекты, транзиты.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'categories',
      name: 'Категории',
      title: 'Разделы',
      description: 'Планеты в знаках, Планеты в домах, Аспекты, Лунные узлы, Ретрограды.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    calendar: [{
      id: 'hero',
      name: 'Hero',
      title: 'Астрологический календарь',
      description: 'Все важные астрологические события: затмения, ретрограды, ингрессии, полнолуния и новолуния.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'events',
      name: 'События',
      title: 'Ближайшие события',
      description: 'Список предстоящих астрологических событий с датами и описаниями.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    marketplace: [{
      id: 'hero',
      name: 'Hero',
      title: 'Маркетплейс астрологических услуг',
      description: 'Все услуги в одном месте: отчёты, консультации, курсы, вебинары от проверенных астрологов.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'categories',
      name: 'Категории',
      title: 'Категории услуг',
      description: 'Натальные карты, Прогнозы, Совместимость, Консультации, Курсы, Вебинары.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'for-astrologers': [{
      id: 'hero',
      name: 'Hero',
      title: 'Для астрологов',
      description: 'Присоединяйтесь к AstroGuide — крупнейшей платформе астрологических услуг. Находите клиентов и развивайте практику.',
      buttonText: 'Стать астрологом',
      imageUrl: '',
      visible: true
    }, {
      id: 'benefits',
      name: 'Преимущества',
      title: 'Почему AstroGuide',
      description: 'Готовая клиентская база, удобный кабинет, система записей, автоматические выплаты, маркетинговая поддержка.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'how_to_join',
      name: 'Как начать',
      title: 'Как стать астрологом на платформе',
      description: '1. Заполните анкету → 2. Пройдите верификацию → 3. Настройте профиль → 4. Начните принимать клиентов.',
      buttonText: 'Подать заявку',
      imageUrl: '',
      visible: true
    }],
    advertising: [{
      id: 'hero',
      name: 'Hero',
      title: 'Реклама на AstroGuide',
      description: 'Размещайте рекламу на платформе с аудиторией 50 000+ пользователей, интересующихся астрологией.',
      buttonText: 'Обсудить размещение',
      imageUrl: '',
      visible: true
    }, {
      id: 'formats',
      name: 'Форматы рекламы',
      title: 'Рекламные форматы',
      description: 'Баннеры, нативные статьи, спонсорские интеграции, email-рассылки, спецпроекты.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'audience',
      name: 'Аудитория',
      title: 'Наша аудитория',
      description: '80% женщины 25-45 лет, интересы: астрология, саморазвитие, психология, эзотерика.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    login: [{
      id: 'hero',
      name: 'Форма входа',
      title: 'Вход в аккаунт',
      description: 'Войдите в свой аккаунт AstroGuide для доступа к отчётам, консультациям и личному кабинету.',
      buttonText: 'Войти',
      imageUrl: '',
      visible: true
    }, {
      id: 'register',
      name: 'Регистрация',
      title: 'Создать аккаунт',
      description: 'Зарегистрируйтесь для доступа ко всем возможностям платформы.',
      buttonText: 'Зарегистрироваться',
      imageUrl: '',
      visible: true
    }],
    checkout: [{
      id: 'form',
      name: 'Форма заказа',
      title: 'Оформление заказа',
      description: 'Корзина, данные для расчёта, выбор способа оплаты, промокод.',
      buttonText: 'Оплатить',
      imageUrl: '',
      visible: true
    }, {
      id: 'payment',
      name: 'Оплата',
      title: 'Способы оплаты',
      description: 'Банковские карты (Visa, MasterCard, МИР), ЮKassa, Apple Pay, Google Pay.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'dashboard-user': [{
      id: 'overview',
      name: 'Обзор',
      title: 'Личный кабинет',
      description: 'Ваши отчёты, заказы, консультации, подписка и настройки профиля.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'reports',
      name: 'Мои отчёты',
      title: 'Мои отчёты',
      description: 'Список всех приобретённых отчётов с возможностью скачивания и просмотра.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'consultations',
      name: 'Консультации',
      title: 'Мои консультации',
      description: 'Предстоящие и прошедшие консультации с астрологами.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }],
    'dashboard-astrologer': [{
      id: 'overview',
      name: 'Обзор',
      title: 'Кабинет астролога',
      description: 'Статистика, записи клиентов, заработок, настройки профиля и расписания.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'schedule',
      name: 'Расписание',
      title: 'Моё расписание',
      description: 'Управление доступными слотами для консультаций.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'clients',
      name: 'Клиенты',
      title: 'Мои клиенты',
      description: 'Список клиентов, история консультаций, заметки.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }, {
      id: 'earnings',
      name: 'Заработок',
      title: 'Финансы',
      description: 'Баланс, история выплат, статистика заработка.',
      buttonText: '',
      imageUrl: '',
      visible: true
    }]
  });
  // --- BLOG STATE (shared via BlogContext with localStorage persistence) ---
  // DEFAULT_CMS_POSTS now live in BlogContext and persist via localStorage
  const blogPosts = cmsPosts;
  const setBlogPosts = setCmsPosts;
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [postForm, setPostForm] = useState({
    title: '',
    excerpt: '',
    category: 'Астрология',
    status: 'draft',
    content: '',
    seoTitle: '',
    seoDescription: '',
    seoKeywords: '',
    imageUrl: '',
    tags: [] as string[]
  });
  const [tagInput, setTagInput] = useState('');
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const [blogSearch, setBlogSearch] = useState('');
  const [blogCategoryFilter, setBlogCategoryFilter] = useState('all');
  const [blogStatusFilter, setBlogStatusFilter] = useState('all');
  const BLOG_CAT_STORAGE_KEY = 'astroguide_blog_categories';
  const defaultBlogCategories = ['Астрология', 'Обучение', 'Календарь', 'Прогнозы', 'Советы', 'Практики', 'Новости'];
  const [blogCategories, setBlogCategories] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(BLOG_CAT_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {}
    return defaultBlogCategories;
  });
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const addBlogCategory = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed || blogCategories.includes(trimmed)) return;
    const updated = [...blogCategories, trimmed];
    setBlogCategories(updated);
    try {
      localStorage.setItem(BLOG_CAT_STORAGE_KEY, JSON.stringify(updated));
    } catch {}
    setNewCategoryInput('');
    setShowNewCategoryInput(false);
    success(`Категория «${trimmed}» добавлена`);
  };
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const removeBlogCategory = (cat: string) => {
    const articlesInCategory = blogPosts.filter(p => p.category === cat);
    if (articlesInCategory.length > 0) {
      setCategoryToDelete(cat);
      return;
    }
    performCategoryDeletion(cat);
  };
  const performCategoryDeletion = (cat: string) => {
    // Update all articles in this category to "Без категории"
    setBlogPosts(prev => prev.map(p => p.category === cat ? {
      ...p,
      category: 'Без категории'
    } : p));
    // Remove category from list
    const updated = blogCategories.filter(c => c !== cat);
    setBlogCategories(updated);
    try {
      localStorage.setItem(BLOG_CAT_STORAGE_KEY, JSON.stringify(updated));
    } catch {}
    // Reset filter if we were filtering by this category
    if (blogCategoryFilter === cat) {
      setBlogCategoryFilter('all');
    }
    setCategoryToDelete(null);
    success(`Категория «${cat}» удалена`);
  };
  const filteredBlogPosts = blogPosts.filter(p => {
    if (blogCategoryFilter !== 'all' && p.category !== blogCategoryFilter) return false;
    if (blogStatusFilter !== 'all' && p.status !== blogStatusFilter) return false;
    if (blogSearch.trim()) {
      const q = blogSearch.toLowerCase();
      if (!p.title.toLowerCase().includes(q) && !p.excerpt.toLowerCase().includes(q)) return false;
    }
    return true;
  });
  // --- MEDIA STATE ---
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([{
    id: 1,
    name: 'hero-banner.jpg',
    type: 'image',
    size: '2.4 MB',
    date: '14 Мар 2026',
    url: '/uploads/hero.jpg',
    color: 'bg-blue-100'
  }, {
    id: 2,
    name: 'natal-chart-example.png',
    type: 'image',
    size: '1.8 MB',
    date: '12 Мар 2026',
    url: '/uploads/natal.png',
    color: 'bg-green-100'
  }, {
    id: 3,
    name: 'promo-video.mp4',
    type: 'video',
    size: '45 MB',
    date: '10 Мар 2026',
    url: '/uploads/promo.mp4',
    color: 'bg-rose-100'
  }, {
    id: 4,
    name: 'terms.pdf',
    type: 'document',
    size: '320 KB',
    date: '01 Мар 2026',
    url: '/uploads/terms.pdf',
    color: 'bg-amber-100'
  }, {
    id: 5,
    name: 'astrologer-photo.jpg',
    type: 'image',
    size: '1.1 MB',
    date: '28 Фев 2026',
    url: '/uploads/astrologer.jpg',
    color: 'bg-purple-100'
  }, {
    id: 6,
    name: 'report-sample.pdf',
    type: 'document',
    size: '2.1 MB',
    date: '25 Фев 2026',
    url: '/uploads/report.pdf',
    color: 'bg-amber-100'
  }]);
  const [mediaFilter, setMediaFilter] = useState<string>('all');
  // --- REVIEWS STATE ---
  const [cmsReviews, setCmsReviews] = useState<ReviewItem[]>([{
    id: 1,
    author: 'Анна К.',
    rating: 5,
    text: 'Потрясающий разбор натальной карты! Очень точно и подробно.',
    date: '14 Мар 2026',
    status: 'published',
    service: 'Натальная карта'
  }, {
    id: 2,
    author: 'Дмитрий П.',
    rating: 4,
    text: 'Хороший прогноз, многое совпало. Рекомендую.',
    date: '12 Мар 2026',
    status: 'published',
    service: 'Прогноз транзитов'
  }, {
    id: 3,
    author: 'Мария С.',
    rating: 5,
    text: 'Консультация была очень полезной, астролог ответил на все вопросы.',
    date: '10 Мар 2026',
    status: 'pending',
    service: 'Консультация'
  }, {
    id: 4,
    author: 'Иван Р.',
    rating: 3,
    text: 'Неплохо, но ожидал более детального анализа.',
    date: '08 Мар 2026',
    status: 'pending',
    service: 'Соляр'
  }, {
    id: 5,
    author: 'Елена В.',
    rating: 5,
    text: 'Лучший сервис! Пользуюсь уже полгода.',
    date: '05 Мар 2026',
    status: 'published',
    service: 'Натальная карта'
  }, {
    id: 6,
    author: 'Спам бот',
    rating: 1,
    text: 'Купите наши товары по ссылке...',
    date: '04 Мар 2026',
    status: 'rejected',
    service: 'Натальная карта'
  }]);
  // --- FAQ STATE ---
  const [faqItems, setFaqItems] = useState<FaqItem[]>([{
    id: 1,
    category: 'Общие вопросы',
    question: 'Что такое натальная карта?',
    answer: 'Натальная карта — это карта неба в момент вашего рождения, показывающая расположение планет.'
  }, {
    id: 2,
    category: 'Общие вопросы',
    question: 'Нужно ли точное время рождения?',
    answer: 'Да, точное время рождения важно для определения Асцендента и домов.'
  }, {
    id: 3,
    category: 'Оплата',
    question: 'Какие способы оплаты доступны?',
    answer: 'Мы принимаем банковские карты, ЮKassa, Apple Pay и Google Pay.'
  }, {
    id: 4,
    category: 'Оплата',
    question: 'Можно ли вернуть деньги?',
    answer: 'Да, в течение 14 дней с момента покупки, если отчёт не был скачан.'
  }, {
    id: 5,
    category: 'Услуги',
    question: 'Чем отличается авто-отчёт от ручного?',
    answer: 'Авто-отчёт генерируется мгновенно алгоритмом. Ручной разбор делает астролог лично.'
  }, {
    id: 6,
    category: 'Услуги',
    question: 'Как долго ждать ручной разбор?',
    answer: 'Обычно 3-5 рабочих дней в зависимости от загруженности астролога.'
  }]);
  // --- PROMOTIONS STATE ---
  const [promotions, setPromotions] = useState<Promotion[]>([{
    id: 1,
    name: 'Весенняя скидка',
    discount: '20%',
    startDate: '01 Мар 2026',
    endDate: '31 Мар 2026',
    status: 'active',
    usageCount: 45,
    code: 'SPRING20'
  }, {
    id: 2,
    name: 'Первый заказ',
    discount: '15%',
    startDate: '01 Янв 2026',
    endDate: '31 Дек 2026',
    status: 'active',
    usageCount: 230,
    code: 'FIRST15'
  }, {
    id: 3,
    name: 'Летний марафон',
    discount: '30%',
    startDate: '01 Июн 2026',
    endDate: '30 Июн 2026',
    status: 'scheduled',
    usageCount: 0,
    code: 'SUMMER30'
  }]);
  const [showPromoForm, setShowPromoForm] = useState(false);
  const [promoForm, setPromoForm] = useState({
    name: '',
    code: '',
    discount: '10%',
    startDate: '',
    endDate: ''
  });
  // --- SECTION HANDLERS (per-page) ---
  const currentSections = selectedPage ? pageSections[selectedPage.id] || [] : [];
  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    if (!selectedPage) return;
    const pageId = selectedPage.id;
    const secs = [...(pageSections[pageId] || [])];
    const swapIdx = direction === 'up' ? index - 1 : index + 1;
    if (swapIdx < 0 || swapIdx >= secs.length) return;
    [secs[index], secs[swapIdx]] = [secs[swapIdx], secs[index]];
    setPageSections(prev => ({
      ...prev,
      [pageId]: secs
    }));
  };
  const handleToggleVisibility = (index: number) => {
    if (!selectedPage) return;
    const pageId = selectedPage.id;
    setPageSections(prev => ({
      ...prev,
      [pageId]: (prev[pageId] || []).map((s, i) => i === index ? {
        ...s,
        visible: !s.visible
      } : s)
    }));
  };
  const handleSectionChange = (index: number, field: string, value: string) => {
    if (!selectedPage) return;
    const pageId = selectedPage.id;
    setPageSections(prev => ({
      ...prev,
      [pageId]: (prev[pageId] || []).map((s, i) => i === index ? {
        ...s,
        [field]: value
      } : s)
    }));
  };
  const handleAddSection = () => {
    if (!selectedPage) return;
    const pageId = selectedPage.id;
    setPageSections(prev => ({
      ...prev,
      [pageId]: [...(prev[pageId] || []), {
        id: `section_${Date.now()}`,
        name: 'Новый блок',
        title: '',
        description: '',
        buttonText: '',
        imageUrl: '',
        visible: true
      }]
    }));
    success('Блок добавлен');
  };
  const handleDeleteSection = (index: number) => {
    if (!selectedPage) return;
    const pageId = selectedPage.id;
    setPageSections(prev => ({
      ...prev,
      [pageId]: (prev[pageId] || []).filter((_, i) => i !== index)
    }));
    success('Блок удалён');
  };
  const pageGroups = [...new Set(pages.map(p => p.group))];
  const filteredPages = pagesFilter === 'all' ? pages : pages.filter(p => p.group === pagesFilter);
  const openPostEditor = (post: BlogPost | null) => {
    if (post) {
      setSelectedPost(post);
      setPostForm({
        title: post.title,
        excerpt: post.excerpt,
        category: post.category,
        status: post.status,
        content: post.content,
        seoTitle: post.seoTitle,
        seoDescription: post.seoDescription,
        seoKeywords: post.seoKeywords,
        imageUrl: post.imageUrl,
        tags: post.tags || []
      });
    } else {
      setSelectedPost(null);
      setPostForm({
        title: '',
        excerpt: '',
        category: 'Астрология',
        status: 'draft',
        content: '',
        seoTitle: '',
        seoDescription: '',
        seoKeywords: '',
        imageUrl: '',
        tags: []
      });
    }
    setTagInput('');
    setCurrentView('blog_editor');
  };
  const handleSavePost = (overrideStatus?: string) => {
    if (!postForm.title.trim()) return;
    const formData = overrideStatus ? {
      ...postForm,
      status: overrideStatus
    } : postForm;
    if (selectedPost) {
      setBlogPosts(prev => prev.map(p => p.id === selectedPost.id ? {
        ...p,
        ...formData,
        date: 'Сегодня'
      } : p));
      success(overrideStatus === 'published' ? 'Статья опубликована' : 'Статья обновлена');
    } else {
      setBlogPosts(prev => [{
        id: Date.now(),
        ...formData,
        date: 'Сегодня',
        views: 0
      }, ...prev]);
      success(overrideStatus === 'published' ? 'Статья опубликована' : 'Статья создана');
    }
    setCurrentView('blog');
  };
  const handleDeletePost = (id: number) => {
    setBlogPosts(prev => prev.filter(p => p.id !== id));
    success('Статья удалена');
  };
  const handleTogglePostStatus = (id: number) => {
    setBlogPosts(prev => prev.map(p => p.id === id ? {
      ...p,
      status: p.status === 'published' ? 'draft' : 'published',
      date: 'Сегодня'
    } : p));
    const post = blogPosts.find(p => p.id === id);
    success(post?.status === 'published' ? 'Статья снята с публикации' : 'Статья опубликована');
  };
  const handleDuplicatePost = (post: BlogPost) => {
    setBlogPosts(prev => [{
      ...post,
      id: Date.now(),
      title: `${post.title} (копия)`,
      status: 'draft',
      date: 'Сегодня',
      views: 0
    }, ...prev]);
    success('Статья скопирована');
  };
  const handleReviewAction = (id: number, action: 'publish' | 'reject' | 'delete') => {
    if (action === 'delete') {
      setCmsReviews(prev => prev.filter(r => r.id !== id));
      success('Отзыв удалён');
    } else {
      setCmsReviews(prev => prev.map(r => r.id === id ? {
        ...r,
        status: action === 'publish' ? 'published' : 'rejected'
      } : r));
      success(action === 'publish' ? 'Отзыв опубликован' : 'Отзыв отклонён');
    }
  };
  const handleFaqChange = (id: number, field: 'question' | 'answer', value: string) => {
    setFaqItems(prev => prev.map(f => f.id === id ? {
      ...f,
      [field]: value
    } : f));
  };
  const handleAddFaq = (category: string) => {
    setFaqItems(prev => [...prev, {
      id: Date.now(),
      category,
      question: '',
      answer: ''
    }]);
  };
  const handleDeleteFaq = (id: number) => {
    setFaqItems(prev => prev.filter(f => f.id !== id));
    success('Вопрос удалён');
  };
  const handleDeleteMedia = (id: number) => {
    setMediaItems(prev => prev.filter(m => m.id !== id));
    success('Файл удалён');
  };
  const handleCreatePromo = () => {
    if (!promoForm.name.trim() || !promoForm.code.trim()) return;
    setPromotions(prev => [...prev, {
      id: Date.now(),
      name: promoForm.name,
      discount: promoForm.discount,
      startDate: promoForm.startDate,
      endDate: promoForm.endDate,
      status: 'active',
      usageCount: 0,
      code: promoForm.code.toUpperCase()
    }]);
    setPromoForm({
      name: '',
      discount: '',
      startDate: '',
      endDate: '',
      code: ''
    });
    setShowPromoForm(false);
    success('Акция создана');
  };
  const handleDeletePromo = (id: number) => {
    setPromotions(prev => prev.filter(p => p.id !== id));
    success('Акция удалена');
  };
  const filteredMedia = mediaFilter === 'all' ? mediaItems : mediaItems.filter(m => m.type === mediaFilter);
  const faqCategories = [...new Set(faqItems.map(f => f.category))];
  const renderStars = (rating: number) => <div className="flex gap-0.5" data-id="element-9650">
      {[1, 2, 3, 4, 5].map(s => <Star key={s} className={`w-3 h-3 ${s <= rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} data-id="element-9651" />)}
    </div>;
  const backButton = (label: string, target: CmsView = 'dashboard') => <div className="flex items-center gap-4 mb-6" data-id="element-9652">
      <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" data-id="element-9654" />} onClick={() => setCurrentView(target)} data-id="element-9653">
        Назад
      </Button>
      <h2 className="text-xl font-bold text-slate-900" data-id="element-9655">{label}</h2>
    </div>;
  // ==================== DASHBOARD ====================
  const renderDashboard = () => <div className="space-y-8" data-id="element-9656">
      <div data-id="element-9657">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-9658">Контент</h2>
        <p className="text-slate-500 mt-1" data-id="element-9659">
          Управление контентом сайта и маркетинговыми материалами
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-9660">
        <Card hoverable padding="md" onClick={() => setCurrentView('pages')} className="flex flex-col" data-id="element-9661">
          <div className="flex justify-between items-start mb-4" data-id="element-9662">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600" data-id="element-9663">
              <FileText className="w-5 h-5" data-id="element-9664" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-9665">
              {pages.length} страниц
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-9666">Страницы</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-9667">
            Управление страницами сайта: главная, о нас, контакты
          </p>
        </Card>

        <Card hoverable padding="md" onClick={() => setCurrentView('blog')} className="flex flex-col" data-id="element-9668">
          <div className="flex justify-between items-start mb-4" data-id="element-9669">
            <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center text-orange-600" data-id="element-9670">
              <BookOpen className="w-5 h-5" data-id="element-9671" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-9672">
              {blogPosts.length} статей
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-9673">Блог</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-9674">
            Публикация статей, новостей и полезных материалов
          </p>
        </Card>

        <Card hoverable padding="md" onClick={() => setCurrentView('media')} className="flex flex-col" data-id="element-9675">
          <div className="flex justify-between items-start mb-4" data-id="element-9676">
            <div className="w-10 h-10 rounded-lg bg-purple-50 flex items-center justify-center text-purple-600" data-id="element-9677">
              <ImageIcon className="w-5 h-5" data-id="element-9678" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-9679">
              {mediaItems.length} файлов
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-9680">Медиа</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-9681">
            Библиотека изображений, видео и документов
          </p>
        </Card>

        <Card hoverable padding="md" onClick={() => setCurrentView('reviews')} className="flex flex-col" data-id="element-9682">
          <div className="flex justify-between items-start mb-4" data-id="element-9683">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600" data-id="element-9684">
              <Star className="w-5 h-5" data-id="element-9685" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-9686">
              {cmsReviews.length} отзывов
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-9687">Отзывы</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-9688">
            Модерация и публикация отзывов клиентов
          </p>
        </Card>

        <Card hoverable padding="md" onClick={() => setCurrentView('faq')} className="flex flex-col" data-id="element-9689">
          <div className="flex justify-between items-start mb-4" data-id="element-9690">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600" data-id="element-9691">
              <HelpCircle className="w-5 h-5" data-id="element-9692" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-9693">
              {faqItems.length} вопросов
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-9694">FAQ</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-9695">
            Управление часто задаваемыми вопросами
          </p>
        </Card>

        <Card hoverable padding="md" onClick={() => setCurrentView('promotions')} className="flex flex-col" data-id="element-9696">
          <div className="flex justify-between items-start mb-4" data-id="element-9697">
            <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center text-rose-600" data-id="element-9698">
              <Megaphone className="w-5 h-5" data-id="element-9699" />
            </div>
            <Badge variant="neutral" size="sm" data-id="element-9700">
              {promotions.filter(p => p.status === 'active').length} активных
            </Badge>
          </div>
          <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-9701">Акции</h3>
          <p className="text-sm text-slate-500 flex-grow" data-id="element-9702">
            Создание и управление промо-акциями и скидками
          </p>
        </Card>
      </div>

      <div className="pt-4" data-id="element-9703">
        <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-9704">
          Обзор контента
        </h3>
        <p className="text-sm text-slate-500 mb-4" data-id="element-9705">
          Общая статистика по всем разделам
        </p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-9706">
          <Card padding="md" data-id="element-9707">
            <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-9708">
              {pages.length + blogPosts.length + faqItems.length + cmsReviews.length + mediaItems.length}
            </div>
            <div className="text-sm text-slate-500" data-id="element-9709">
              Всего элементов контента
            </div>
          </Card>
          <Card padding="md" data-id="element-9710">
            <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-9711">
              {blogPosts.filter(p => p.status === 'published').length}
            </div>
            <div className="text-sm text-slate-500" data-id="element-9712">Опубликовано статей</div>
          </Card>
          <Card padding="md" data-id="element-9713">
            <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-9714">
              {blogPosts.filter(p => p.status === 'draft').length}
            </div>
            <div className="text-sm text-slate-500" data-id="element-9715">Черновики</div>
          </Card>
          <Card padding="md" data-id="element-9716">
            <div className="text-3xl font-bold text-slate-900 mb-1" data-id="element-9717">
              {cmsReviews.filter(r => r.status === 'pending').length}
            </div>
            <div className="text-sm text-slate-500" data-id="element-9718">Ожидают модерации</div>
          </Card>
        </div>
      </div>
    </div>;
  // ==================== PAGES LIST ====================
  const renderPagesList = () => <div className="space-y-6" data-id="element-9719">
      {backButton('Страницы сайта')}
      <p className="text-sm text-slate-500 -mt-4" data-id="element-9720">
        Управление контентом {pages.length} страниц сайта
      </p>

      <div className="flex gap-2 flex-wrap" data-id="element-9721">
        <button onClick={() => setPagesFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${pagesFilter === 'all' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-9722">
          Все ({pages.length})
        </button>
        {pageGroups.map(group => <button key={group} onClick={() => setPagesFilter(group)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${pagesFilter === group ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-9723">
            {group} ({pages.filter(p => p.group === group).length})
          </button>)}
      </div>

      <Card padding="none" className="overflow-hidden" data-id="element-9724">
        <table className="w-full text-left" data-id="element-9725">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500" data-id="element-9726">
            <tr data-id="element-9727">
              <th className="px-4 py-3 font-medium" data-id="element-9728">Название</th>
              <th className="px-4 py-3 font-medium" data-id="element-9729">URL</th>
              <th className="px-4 py-3 font-medium" data-id="element-9730">Группа</th>
              <th className="px-4 py-3 font-medium" data-id="element-9731">Статус</th>
              <th className="px-4 py-3 font-medium" data-id="element-9732">Блоков</th>
              <th className="px-4 py-3 font-medium" data-id="element-9733">Изменено</th>
              <th className="px-4 py-3 text-right font-medium" data-id="element-9734">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-9735">
            {filteredPages.map(page => <tr key={page.id} className="hover:bg-slate-50 transition-colors" data-id="element-9736">
                <td className="px-4 py-3 font-medium text-slate-900" data-id="element-9737">
                  {page.title}
                </td>
                <td className="px-4 py-3 text-sm text-slate-500 font-mono text-xs" data-id="element-9738">
                  {page.slug}
                </td>
                <td className="px-4 py-3" data-id="element-9739">
                  <Badge variant="neutral" size="sm" data-id="element-9740">
                    {page.group}
                  </Badge>
                </td>
                <td className="px-4 py-3" data-id="element-9741">
                  <Badge variant={page.status === 'published' ? 'success' : 'warning'} size="sm" data-id="element-9742">
                    {page.status === 'published' ? 'Опубликовано' : 'Черновик'}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm text-slate-500" data-id="element-9743">
                  {(pageSections[page.id] || []).length}
                </td>
                <td className="px-4 py-3 text-sm text-slate-500" data-id="element-9744">
                  {page.lastModified}
                </td>
                <td className="px-4 py-3 text-right" data-id="element-9745">
                  <div className="flex justify-end gap-2" data-id="element-9746">
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0" icon={<Eye className="w-4 h-4" data-id="element-9748" />} onClick={() => onNavigate(page.id)} title="Просмотр" data-id="element-9747" />
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-indigo-600" icon={<Edit2 className="w-4 h-4" data-id="element-9750" />} onClick={() => {
                  setSelectedPage(page);
                  setCurrentView('page_editor');
                }} title="Редактировать" data-id="element-9749" />
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </Card>
    </div>;
  // ==================== PAGE EDITOR ====================
  const renderPageEditor = () => <div className="space-y-6" data-id="element-9751">
      <div className="flex items-center justify-between" data-id="element-9752">
        <div className="flex items-center gap-4" data-id="element-9753">
          <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" data-id="element-9755" />} onClick={() => setCurrentView('pages')} data-id="element-9754">
            Назад
          </Button>
          <div data-id="element-9756">
            <h2 className="text-xl font-bold text-slate-900" data-id="element-9757">
              Редактирование: {selectedPage?.title}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-9758">
              {selectedPage?.slug} · {currentSections.length} блоков
            </p>
          </div>
        </div>
        <div className="flex gap-3" data-id="element-9759">
          <Button variant="outline" icon={<Eye className="w-4 h-4" data-id="element-9761" />} onClick={() => onNavigate(selectedPage?.id || 'home')} data-id="element-9760">
            Предпросмотр
          </Button>
          <Button variant="primary" icon={<Save className="w-4 h-4" data-id="element-9763" />} onClick={() => success('Изменения сохранены')} data-id="element-9762">
            Сохранить
          </Button>
        </div>
      </div>

      <div className="space-y-4" data-id="element-9764">
        {currentSections.map((section, index) => <Card key={section.id} padding="md" className={`transition-opacity ${section.visible ? 'opacity-100' : 'opacity-50'}`} data-id="element-9765">
            <div className="flex items-center justify-between mb-4 pb-4 border-b border-slate-100" data-id="element-9766">
              <div className="flex items-center gap-3" data-id="element-9767">
                <div className="flex flex-col gap-0.5" data-id="element-9768">
                  <button onClick={() => handleMoveSection(index, 'up')} disabled={index === 0} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30 transition-colors" data-id="element-9769">
                    <ChevronUp className="w-4 h-4" data-id="element-9770" />
                  </button>
                  <button onClick={() => handleMoveSection(index, 'down')} disabled={index === currentSections.length - 1} className="text-slate-400 hover:text-indigo-600 disabled:opacity-30 transition-colors" data-id="element-9771">
                    <ChevronDown className="w-4 h-4" data-id="element-9772" />
                  </button>
                </div>
                <h3 className="text-base font-bold text-slate-900" data-id="element-9773">
                  Блок: {section.name}
                </h3>
                {!section.visible && <Badge variant="neutral" size="sm" data-id="element-9774">
                    Скрыт
                  </Badge>}
              </div>
              <div className="flex items-center gap-1" data-id="element-9775">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" icon={section.visible ? <EyeOff className="w-4 h-4" data-id="element-9777" /> : <Eye className="w-4 h-4" data-id="element-9778" />} onClick={() => handleToggleVisibility(index)} title={section.visible ? 'Скрыть' : 'Показать'} data-id="element-9776" />
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-rose-500" icon={<Trash2 className="w-4 h-4" data-id="element-9780" />} onClick={() => handleDeleteSection(index)} title="Удалить" data-id="element-9779" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9781">
              <div className="space-y-4" data-id="element-9782">
                <Input label="Заголовок" value={section.title} onChange={e => handleSectionChange(index, 'title', e.target.value)} data-id="element-9783" />
                <div data-id="element-9784">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9785">
                    Описание / Текст
                  </label>
                  <textarea className="w-full rounded-xl border-slate-200 shadow-sm px-4 py-3 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-y min-h-[100px] text-sm" value={section.description} onChange={e => handleSectionChange(index, 'description', e.target.value)} data-id="element-9786" />
                </div>
              </div>
              <div className="space-y-4" data-id="element-9787">
                <Input label="Текст кнопки" value={section.buttonText} onChange={e => handleSectionChange(index, 'buttonText', e.target.value)} placeholder="Оставьте пустым, если кнопка не нужна" data-id="element-9788" />
                <ImageUpload label="Изображение блока" value={section.imageUrl} onChange={url => handleSectionChange(index, 'imageUrl', url)} height="h-28" data-id="element-9789" />
              </div>
            </div>
          </Card>)}
        <button onClick={handleAddSection} className="w-full flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all font-medium text-sm" data-id="element-9790">
          <Plus className="w-4 h-4" data-id="element-9791" /> Добавить блок
        </button>
      </div>
    </div>;
  // ==================== BLOG LIST ====================
  const renderBlogList = () => {
    const publishedCount = blogPosts.filter(p => p.status === 'published').length;
    const draftCount = blogPosts.filter(p => p.status === 'draft').length;
    const totalViews = blogPosts.reduce((sum, p) => sum + p.views, 0);
    return <div className="space-y-6" data-id="element-9792">
        <div className="flex items-center justify-between" data-id="element-9793">
          {backButton('Блог')}
          <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-9795" />} onClick={() => openPostEditor(null)} data-id="element-9794">
            Создать статью
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-9796">
          <Card padding="sm" data-id="element-9797">
            <div className="text-2xl font-bold text-slate-900" data-id="element-9798">
              {blogPosts.length}
            </div>
            <div className="text-xs text-slate-500" data-id="element-9799">Всего статей</div>
          </Card>
          <Card padding="sm" data-id="element-9800">
            <div className="text-2xl font-bold text-emerald-600" data-id="element-9801">
              {publishedCount}
            </div>
            <div className="text-xs text-slate-500" data-id="element-9802">Опубликовано</div>
          </Card>
          <Card padding="sm" data-id="element-9803">
            <div className="text-2xl font-bold text-amber-600" data-id="element-9804">
              {draftCount}
            </div>
            <div className="text-xs text-slate-500" data-id="element-9805">Черновики</div>
          </Card>
          <Card padding="sm" data-id="element-9806">
            <div className="text-2xl font-bold text-indigo-600" data-id="element-9807">
              {totalViews.toLocaleString()}
            </div>
            <div className="text-xs text-slate-500" data-id="element-9808">Просмотров</div>
          </Card>
        </div>

        {/* Search & Filters */}
        <div className="space-y-3" data-id="element-9809">
          <Input icon={<Search className="w-4 h-4" data-id="element-9811" />} value={blogSearch} onChange={e => setBlogSearch(e.target.value)} placeholder="Поиск по заголовку или описанию..." data-id="element-9810" />
          <div className="flex flex-wrap gap-2" data-id="element-9812">
            <div className="flex gap-1 flex-wrap" data-id="element-9813">
              <button onClick={() => setBlogStatusFilter('all')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${blogStatusFilter === 'all' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-9814">
                Все ({blogPosts.length})
              </button>
              <button onClick={() => setBlogStatusFilter('published')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${blogStatusFilter === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-9815">
                Опубликовано ({publishedCount})
              </button>
              <button onClick={() => setBlogStatusFilter('draft')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${blogStatusFilter === 'draft' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-9816">
                Черновики ({draftCount})
              </button>
            </div>
            <div className="h-6 w-px bg-slate-200 hidden sm:block self-center" data-id="element-9817" />
            <div className="flex gap-1 flex-wrap items-center" data-id="element-9818">
              <button onClick={() => setBlogCategoryFilter('all')} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${blogCategoryFilter === 'all' ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-9819">
                Все категории
              </button>
              {blogCategories.map(cat => <button key={cat} onClick={() => setBlogCategoryFilter(cat)} className={`px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${blogCategoryFilter === cat ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'} group relative`} data-id="element-9820">
                  {cat}
                  <span onClick={e => {
                e.stopPropagation();
                removeBlogCategory(cat);
              }} className="ml-1 text-slate-400 hover:text-rose-500 hidden group-hover:inline" title="Удалить категорию" data-id="element-9821">
                    ×
                  </span>
                </button>)}
              {showNewCategoryInput ? <div className="flex items-center gap-1" data-id="element-9822">
                  <input autoFocus value={newCategoryInput} onChange={e => setNewCategoryInput(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') addBlogCategory(newCategoryInput);
                if (e.key === 'Escape') {
                  setShowNewCategoryInput(false);
                  setNewCategoryInput('');
                }
              }} placeholder="Название..." className="px-2 py-1 rounded-lg border border-slate-200 text-xs w-28 outline-none focus:border-indigo-400" data-id="element-9823" />
                  <button onClick={() => addBlogCategory(newCategoryInput)} className="px-2 py-1 rounded-lg bg-indigo-100 text-indigo-700 text-xs font-medium hover:bg-indigo-200" data-id="element-9824">
                    OK
                  </button>
                  <button onClick={() => {
                setShowNewCategoryInput(false);
                setNewCategoryInput('');
              }} className="px-1.5 py-1 rounded-lg text-slate-400 hover:text-slate-600 text-xs" data-id="element-9825">
                    ✕
                  </button>
                </div> : <button onClick={() => setShowNewCategoryInput(true)} className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-dashed border-slate-300 text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors" data-id="element-9826">
                  + Категория
                </button>}
            </div>
          </div>
        </div>

        {/* Table */}
        {filteredBlogPosts.length > 0 ? <Card padding="none" className="overflow-hidden" data-id="element-9827">
            <table className="w-full text-left" data-id="element-9828">
              <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500" data-id="element-9829">
                <tr data-id="element-9830">
                  <th className="px-4 py-3 font-medium" data-id="element-9831">Заголовок</th>
                  <th className="px-4 py-3 font-medium" data-id="element-9832">Категория</th>
                  <th className="px-4 py-3 font-medium" data-id="element-9833">Статус</th>
                  <th className="px-4 py-3 font-medium" data-id="element-9834">Дата</th>
                  <th className="px-4 py-3 font-medium" data-id="element-9835">Просмотры</th>
                  <th className="px-4 py-3 text-right font-medium" data-id="element-9836">Действия</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-9837">
                {filteredBlogPosts.map(post => <tr key={post.id} className="hover:bg-slate-50 transition-colors" data-id="element-9838">
                    <td className="px-4 py-3" data-id="element-9839">
                      <div className="flex items-center gap-3" data-id="element-9840">
                        {post.imageUrl && <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0" data-id="element-9841">
                            <img src={post.imageUrl} alt="" className="w-full h-full object-cover" data-id="element-9842" />
                          </div>}
                        <div data-id="element-9843">
                          <div className="font-medium text-slate-900 text-sm flex items-center gap-1.5" data-id="element-9844">
                            {post.featured && <Pin className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" data-id="element-9845" />}
                            {post.title}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5 line-clamp-1" data-id="element-9846">
                            {post.excerpt}
                          </div>
                          {post.tags && post.tags.length > 0 && <div className="flex gap-1 mt-1 flex-wrap" data-id="element-9847">
                              {post.tags.slice(0, 3).map(tag => <span key={tag} className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px]" data-id="element-9848">
                                  {tag}
                                </span>)}
                              {post.tags.length > 3 && <span className="text-[10px] text-slate-400" data-id="element-9849">
                                  +{post.tags.length - 3}
                                </span>}
                            </div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-9850">
                      <Badge variant="neutral" size="sm" data-id="element-9851">
                        {post.category}
                      </Badge>
                    </td>
                    <td className="px-4 py-3" data-id="element-9852">
                      <button onClick={() => handleTogglePostStatus(post.id)} title={post.status === 'published' ? 'Нажмите, чтобы снять с публикации' : 'Нажмите, чтобы опубликовать'} data-id="element-9853">
                        <Badge variant={post.status === 'published' ? 'success' : 'warning'} size="sm" className="cursor-pointer hover:opacity-80 transition-opacity" data-id="element-9854">
                          {post.status === 'published' ? 'Опубликовано' : 'Черновик'}
                        </Badge>
                      </button>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500" data-id="element-9855">
                      {post.date}
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-500" data-id="element-9856">
                      {post.views.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right" data-id="element-9857">
                      <div className="flex justify-end gap-1" data-id="element-9858">
                        <Button variant="ghost" size="sm" className={`h-7 w-7 p-0 ${post.featured ? 'text-amber-500' : 'text-slate-400 hover:text-amber-500'}`} icon={<Pin className="w-3.5 h-3.5" data-id="element-9860" />} onClick={() => {
                    setBlogPosts(prev => prev.map(p => p.id === post.id ? {
                      ...p,
                      featured: !p.featured
                    } : {
                      ...p,
                      featured: false
                    }));
                    success(post.featured ? 'Статья откреплена' : 'Статья закреплена в топе блога');
                  }} title={post.featured ? 'Открепить' : 'Закрепить в топе'} data-id="element-9859" />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-indigo-600" icon={<Edit2 className="w-3.5 h-3.5" data-id="element-9862" />} onClick={() => openPostEditor(post)} title="Редактировать" data-id="element-9861" />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500" icon={<Copy className="w-3.5 h-3.5" data-id="element-9864" />} onClick={() => handleDuplicatePost(post)} title="Дублировать" data-id="element-9863" />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500" icon={<Trash2 className="w-3.5 h-3.5" data-id="element-9866" />} onClick={() => handleDeletePost(post.id)} title="Удалить" data-id="element-9865" />
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </Card> : <Card padding="lg" className="text-center" data-id="element-9867">
            <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" data-id="element-9868" />
            <p className="text-sm text-slate-500 mb-1" data-id="element-9869">
              {blogSearch || blogCategoryFilter !== 'all' || blogStatusFilter !== 'all' ? 'Статьи не найдены' : 'Пока нет статей'}
            </p>
            <p className="text-xs text-slate-400" data-id="element-9870">
              {blogSearch || blogCategoryFilter !== 'all' || blogStatusFilter !== 'all' ? 'Попробуйте изменить фильтры' : 'Создайте первую статью'}
            </p>
          </Card>}
      </div>;
  };
  // ==================== BLOG EDITOR ====================
  const insertFormat = (prefix: string, suffix: string = '', placeholder: string = '') => {
    const ta = contentRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const text = postForm.content;
    const selected = text.substring(start, end);
    const insert = selected || placeholder;
    const newText = text.substring(0, start) + prefix + insert + suffix + text.substring(end);
    setPostForm(p => ({
      ...p,
      content: newText
    }));
    setTimeout(() => {
      ta.focus();
      const cursorPos = start + prefix.length + insert.length + suffix.length;
      ta.setSelectionRange(cursorPos, cursorPos);
    }, 0);
  };
  const insertLinePrefix = (prefix: string) => {
    const ta = contentRef.current;
    if (!ta) return;
    const start = ta.selectionStart;
    const text = postForm.content;
    const lineStart = text.lastIndexOf('\n', start - 1) + 1;
    const newText = text.substring(0, lineStart) + prefix + text.substring(lineStart);
    setPostForm(p => ({
      ...p,
      content: newText
    }));
    setTimeout(() => {
      ta.focus();
      ta.setSelectionRange(start + prefix.length, start + prefix.length);
    }, 0);
  };
  const tocHeadings = postForm.content.split('\n').filter(line => line.startsWith('## ') || line.startsWith('### ')).map(line => ({
    level: line.startsWith('### ') ? 3 : 2,
    text: line.replace(/^#{2,3}\s+/, '')
  }));
  const handleAddTag = () => {
    const tag = tagInput.trim();
    if (tag && !postForm.tags.includes(tag)) {
      setPostForm(p => ({
        ...p,
        tags: [...p.tags, tag]
      }));
    }
    setTagInput('');
  };
  const handleRemoveTag = (tag: string) => {
    setPostForm(p => ({
      ...p,
      tags: p.tags.filter(t => t !== tag)
    }));
  };
  const toolbarBtnClass = 'w-8 h-8 flex items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-800 transition-colors text-xs font-bold';
  const renderBlogEditor = () => <div className="space-y-6" data-id="element-9871">
      <div className="flex items-center justify-between flex-wrap gap-3" data-id="element-9872">
        <div className="flex items-center gap-4" data-id="element-9873">
          <Button variant="ghost" size="sm" icon={<ArrowLeft className="w-4 h-4" data-id="element-9875" />} onClick={() => setCurrentView('blog')} data-id="element-9874">
            Назад
          </Button>
          <h2 className="text-xl font-bold text-slate-900" data-id="element-9876">
            {selectedPost ? 'Редактировать статью' : 'Новая статья'}
          </h2>
        </div>
        <div className="flex gap-3" data-id="element-9877">
          {selectedPost && <Button variant="outline" icon={<Eye className="w-4 h-4" data-id="element-9879" />} onClick={() => onNavigate(`blog-post/cms-${selectedPost.id}`)} data-id="element-9878">
              Предпросмотр
            </Button>}
          <Button variant="outline" icon={<Save className="w-4 h-4" data-id="element-9881" />} onClick={handleSavePost} data-id="element-9880">
            Сохранить черновик
          </Button>
          <Button variant="primary" icon={<CheckCircle2 className="w-4 h-4" data-id="element-9883" />} onClick={() => handleSavePost('published')} data-id="element-9882">
            Опубликовать
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-9884">
        <div className="lg:col-span-2 space-y-4" data-id="element-9885">
          <Card padding="md" data-id="element-9886">
            <div className="space-y-4" data-id="element-9887">
              <Input label="Заголовок" value={postForm.title} onChange={e => setPostForm(p => ({
              ...p,
              title: e.target.value
            }))} placeholder="Введите заголовок статьи" data-id="element-9888" />
              <div data-id="element-9889">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9890">
                  Краткое описание
                </label>
                <textarea className="w-full rounded-xl border-slate-200 shadow-sm px-4 py-3 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-y min-h-[80px] text-sm" value={postForm.excerpt} onChange={e => setPostForm(p => ({
                ...p,
                excerpt: e.target.value
              }))} placeholder="Краткое описание для превью..." data-id="element-9891" />
              </div>
              <div data-id="element-9892">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9893">
                  Содержание статьи
                </label>
                {/* Formatting Toolbar */}
                <div className="flex items-center gap-0.5 p-1.5 bg-slate-50 border border-slate-200 border-b-0 rounded-t-xl flex-wrap" data-id="element-9894">
                  <button title="Жирный (Ctrl+B)" className={toolbarBtnClass} onClick={() => insertFormat('**', '**', 'жирный текст')} data-id="element-9895">
                    <span className="font-black text-sm" data-id="element-9896">B</span>
                  </button>
                  <button title="Курсив (Ctrl+I)" className={toolbarBtnClass} onClick={() => insertFormat('*', '*', 'курсив')} data-id="element-9897">
                    <span className="italic text-sm font-serif" data-id="element-9898">I</span>
                  </button>
                  <div className="w-px h-5 bg-slate-200 mx-1" data-id="element-9899" />
                  <button title="Заголовок H2" className={toolbarBtnClass} onClick={() => insertLinePrefix('## ')} data-id="element-9900">
                    <span className="text-[11px] font-black" data-id="element-9901">H2</span>
                  </button>
                  <button title="Заголовок H3" className={toolbarBtnClass} onClick={() => insertLinePrefix('### ')} data-id="element-9902">
                    <span className="text-[11px] font-black" data-id="element-9903">H3</span>
                  </button>
                  <div className="w-px h-5 bg-slate-200 mx-1" data-id="element-9904" />
                  <button title="Маркированный список" className={toolbarBtnClass} onClick={() => insertLinePrefix('- ')} data-id="element-9905">
                    <span className="text-sm" data-id="element-9906">•</span>
                  </button>
                  <button title="Нумерованный список" className={toolbarBtnClass} onClick={() => insertLinePrefix('1. ')} data-id="element-9907">
                    <span className="text-[11px] font-bold" data-id="element-9908">1.</span>
                  </button>
                  <button title="Цитата" className={toolbarBtnClass} onClick={() => insertLinePrefix('> ')} data-id="element-9909">
                    <span className="text-sm font-serif" data-id="element-9910">"</span>
                  </button>
                  <div className="w-px h-5 bg-slate-200 mx-1" data-id="element-9911" />
                  <button title="Ссылка" className={toolbarBtnClass} onClick={() => insertFormat('[', '](url)', 'текст ссылки')} data-id="element-9912">
                    <ExternalLink className="w-3.5 h-3.5" data-id="element-9913" />
                  </button>
                  <button title="Разделитель" className={toolbarBtnClass} onClick={() => insertFormat('\n\n---\n\n', '', '')} data-id="element-9914">
                    <span className="text-[10px] tracking-widest" data-id="element-9915">———</span>
                  </button>
                </div>
                <textarea ref={contentRef} className="w-full rounded-b-xl rounded-t-none border-slate-200 shadow-sm px-4 py-3 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-y min-h-[320px] text-sm font-mono leading-relaxed" value={postForm.content} onChange={e => setPostForm(p => ({
                ...p,
                content: e.target.value
              }))} placeholder="Используйте панель форматирования выше или Markdown-разметку:&#10;&#10;## Заголовок раздела&#10;### Подзаголовок&#10;&#10;Обычный текст. **Жирный** и *курсив*.&#10;&#10;- Пункт списка&#10;- Ещё пункт&#10;&#10;> Цитата&#10;&#10;[Ссылка](https://example.com)" onKeyDown={e => {
                if (e.ctrlKey || e.metaKey) {
                  if (e.key === 'b') {
                    e.preventDefault();
                    insertFormat('**', '**', 'жирный текст');
                  }
                  if (e.key === 'i') {
                    e.preventDefault();
                    insertFormat('*', '*', 'курсив');
                  }
                }
              }} data-id="element-9916" />
                <div className="flex items-center justify-between mt-1.5" data-id="element-9917">
                  <span className="text-[10px] text-slate-400" data-id="element-9918">
                    Поддерживается Markdown-разметка • Ctrl+B — жирный • Ctrl+I
                    — курсив
                  </span>
                  <span className="text-[10px] text-slate-400" data-id="element-9919">
                    {postForm.content.length} символов
                  </span>
                </div>
              </div>
            </div>
          </Card>

          <Card padding="md" data-id="element-9920">
            <h3 className="text-sm font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-9921">
              <Search className="w-4 h-4 text-slate-400" data-id="element-9922" /> SEO настройки
            </h3>
            <div className="space-y-4" data-id="element-9923">
              <Input label="Meta Title" value={postForm.seoTitle} onChange={e => setPostForm(p => ({
              ...p,
              seoTitle: e.target.value
            }))} placeholder="Заголовок для поисковых систем" data-id="element-9924" />
              <div data-id="element-9925">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9926">
                  Meta Description
                </label>
                <textarea className="w-full rounded-xl border-slate-200 shadow-sm px-4 py-3 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-y min-h-[60px] text-sm" value={postForm.seoDescription} onChange={e => setPostForm(p => ({
                ...p,
                seoDescription: e.target.value
              }))} placeholder="Описание для поисковых систем (до 160 символов)" data-id="element-9927" />
              </div>
              <Input label="Ключевые слова" value={postForm.seoKeywords} onChange={e => setPostForm(p => ({
              ...p,
              seoKeywords: e.target.value
            }))} placeholder="ключевое слово 1, ключевое слово 2" data-id="element-9928" />
            </div>
          </Card>
        </div>

        <div className="space-y-4" data-id="element-9929">
          <Card padding="md" data-id="element-9930">
            <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-9931">Параметры</h3>
            <div className="space-y-4" data-id="element-9932">
              <div data-id="element-9933">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9934">
                  Категория
                </label>
                <div className="flex gap-2" data-id="element-9935">
                  <select className="flex-1 rounded-xl border-slate-200 shadow-sm px-3 py-2.5 border outline-none text-sm focus:border-indigo-500" value={postForm.category} onChange={e => {
                  if (e.target.value === '__new__') {
                    setShowNewCategoryInput(true);
                  } else {
                    setPostForm(p => ({
                      ...p,
                      category: e.target.value
                    }));
                  }
                }} data-id="element-9936">
                    {blogCategories.map(cat => <option key={cat} value={cat} data-id="element-9937">
                        {cat}
                      </option>)}
                    <option value="__new__" data-id="element-9938">+ Новая категория...</option>
                  </select>
                </div>
                {showNewCategoryInput && <div className="flex gap-2 mt-2" data-id="element-9939">
                    <input autoFocus value={newCategoryInput} onChange={e => setNewCategoryInput(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && newCategoryInput.trim()) {
                    addBlogCategory(newCategoryInput);
                    setPostForm(p => ({
                      ...p,
                      category: newCategoryInput.trim()
                    }));
                  }
                  if (e.key === 'Escape') {
                    setShowNewCategoryInput(false);
                    setNewCategoryInput('');
                  }
                }} placeholder="Название категории..." className="flex-1 rounded-xl border-slate-200 shadow-sm px-3 py-2 border outline-none text-sm focus:border-indigo-500" data-id="element-9940" />
                    <button onClick={() => {
                  if (newCategoryInput.trim()) {
                    addBlogCategory(newCategoryInput);
                    setPostForm(p => ({
                      ...p,
                      category: newCategoryInput.trim()
                    }));
                  }
                }} className="px-3 py-2 rounded-xl bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 transition-colors" data-id="element-9941">
                      Добавить
                    </button>
                    <button onClick={() => {
                  setShowNewCategoryInput(false);
                  setNewCategoryInput('');
                }} className="px-2 py-2 rounded-xl text-slate-400 hover:text-slate-600 text-sm" data-id="element-9942">
                      ✕
                    </button>
                  </div>}
              </div>
              <div data-id="element-9943">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-9944">
                  Статус
                </label>
                <select className="w-full rounded-xl border-slate-200 shadow-sm px-3 py-2.5 border outline-none text-sm focus:border-indigo-500" value={postForm.status} onChange={e => setPostForm(p => ({
                ...p,
                status: e.target.value
              }))} data-id="element-9945">
                  <option value="draft" data-id="element-9946">Черновик</option>
                  <option value="published" data-id="element-9947">Опубликовано</option>
                </select>
              </div>
            </div>
          </Card>

          {/* Tags */}
          <Card padding="md" data-id="element-9948">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-9949">
              <Tag className="w-4 h-4 text-slate-400" data-id="element-9950" /> Теги
            </h3>
            <div className="flex flex-wrap gap-1.5 mb-3" data-id="element-9951">
              {postForm.tags.map(tag => <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-lg text-xs font-medium" data-id="element-9952">
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="hover:text-indigo-900 transition-colors" data-id="element-9953">
                    <X className="w-3 h-3" data-id="element-9954" />
                  </button>
                </span>)}
              {postForm.tags.length === 0 && <span className="text-xs text-slate-400" data-id="element-9955">Нет тегов</span>}
            </div>
            <div className="flex gap-2" data-id="element-9956">
              <input className="flex-1 rounded-lg border-slate-200 shadow-sm px-3 py-1.5 border outline-none text-sm focus:border-indigo-500" value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddTag();
              }
            }} placeholder="Новый тег..." data-id="element-9957" />
              <Button variant="outline" size="sm" onClick={handleAddTag} icon={<Plus className="w-3.5 h-3.5" data-id="element-9959" />} data-id="element-9958">
                Добавить
              </Button>
            </div>
          </Card>

          {/* Table of Contents */}
          <Card padding="md" data-id="element-9960">
            <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2" data-id="element-9961">
              <FileText className="w-4 h-4 text-slate-400" data-id="element-9962" /> Содержание
            </h3>
            {tocHeadings.length > 0 ? <div className="space-y-1" data-id="element-9963">
                {tocHeadings.map((h, i) => <div key={i} className={`text-sm text-slate-600 py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors cursor-default ${h.level === 3 ? 'pl-6 text-xs text-slate-500' : 'font-medium'}`} data-id="element-9964">
                    <span className="text-slate-300 mr-2" data-id="element-9965">
                      {h.level === 2 ? '§' : '∟'}
                    </span>
                    {h.text}
                  </div>)}
              </div> : <div className="text-center py-4" data-id="element-9966">
                <p className="text-xs text-slate-400 mb-1" data-id="element-9967">Содержание пусто</p>
                <p className="text-[10px] text-slate-300" data-id="element-9968">
                  Добавьте заголовки (## или ###) в текст статьи
                </p>
              </div>}
            <div className="mt-3 pt-3 border-t border-slate-100" data-id="element-9969">
              <p className="text-[10px] text-slate-400 leading-relaxed" data-id="element-9970">
                Содержание формируется автоматически из заголовков H2 и H3 в
                тексте статьи. Используйте кнопки H2/H3 на панели
                форматирования.
              </p>
            </div>
          </Card>

          <Card padding="md" data-id="element-9971">
            <h3 className="text-sm font-bold text-slate-900 mb-4" data-id="element-9972">Обложка</h3>
            <ImageUpload label="" value={postForm.imageUrl} onChange={url => setPostForm(p => ({
            ...p,
            imageUrl: url
          }))} height="h-36" data-id="element-9973" />
          </Card>
        </div>
      </div>
    </div>;
  // ==================== MEDIA LIBRARY ====================
  const renderMediaLibrary = () => {
    const getTypeIcon = (type: string) => {
      switch (type) {
        case 'image':
          return <FileImage className="w-5 h-5 text-blue-500" data-id="element-9974" />;
        case 'video':
          return <FileVideo className="w-5 h-5 text-rose-500" data-id="element-9975" />;
        case 'document':
          return <File className="w-5 h-5 text-amber-500" data-id="element-9976" />;
        default:
          return <File className="w-5 h-5 text-slate-400" data-id="element-9977" />;
      }
    };
    const mediaInputRef = useRef<HTMLInputElement>(null);
    const handleMediaUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const fileType: 'image' | 'video' | 'document' = isImage ? 'image' : isVideo ? 'video' : 'document';
      const colors = {
        image: 'bg-blue-100',
        video: 'bg-rose-100',
        document: 'bg-amber-100'
      };
      const reader = new FileReader();
      reader.onload = ev => {
        const url = ev.target?.result as string;
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        setMediaItems(prev => [{
          id: Date.now(),
          name: file.name,
          type: fileType,
          size: `${sizeMB} MB`,
          date: 'Сегодня',
          url,
          color: colors[fileType]
        }, ...prev]);
        success('Файл загружен');
      };
      reader.readAsDataURL(file);
      if (mediaInputRef.current) mediaInputRef.current.value = '';
    };
    return <div className="space-y-6" data-id="element-9978">
        <input ref={mediaInputRef} type="file" accept="image/*,video/*,.pdf,.doc,.docx" onChange={handleMediaUpload} className="hidden" data-id="element-9979" />
        <div className="flex items-center justify-between" data-id="element-9980">
          {backButton('Медиа библиотека')}
          <Button variant="primary" size="sm" icon={<Upload className="w-4 h-4" data-id="element-9982" />} onClick={() => mediaInputRef.current?.click()} data-id="element-9981">
            Загрузить
          </Button>
        </div>

        <div className="flex gap-2" data-id="element-9983">
          {([['all', 'Все'], ['image', 'Изображения'], ['video', 'Видео'], ['document', 'Документы']] as const).map(([key, label]) => <button key={key} onClick={() => setMediaFilter(key)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${mediaFilter === key ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-9984">
              {label}
            </button>)}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-9985">
          {filteredMedia.map(item => <Card key={item.id} padding="sm" className="group relative" data-id="element-9986">
              <div className={`h-28 rounded-lg ${item.color} flex items-center justify-center mb-3 overflow-hidden`} data-id="element-9987">
                {item.type === 'image' && item.url.startsWith('data:') ? <img src={item.url} alt={item.name} className="w-full h-full object-cover" data-id="element-9988" /> : getTypeIcon(item.type)}
              </div>
              <div className="text-sm font-medium text-slate-900 truncate" data-id="element-9989">
                {item.name}
              </div>
              <div className="flex justify-between items-center mt-1" data-id="element-9990">
                <span className="text-xs text-slate-500" data-id="element-9991">{item.size}</span>
                <span className="text-xs text-slate-400" data-id="element-9992">{item.date}</span>
              </div>
              <div className="flex gap-1 mt-3" data-id="element-9993">
                <Button variant="ghost" size="sm" className="h-7 flex-1 text-xs" icon={<Copy className="w-3 h-3" data-id="element-9995" />} onClick={() => {
              navigator.clipboard?.writeText(item.url);
              success('URL скопирован');
            }} data-id="element-9994">
                  URL
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500" icon={<Trash2 className="w-3 h-3" data-id="element-9997" />} onClick={() => handleDeleteMedia(item.id)} data-id="element-9996" />
              </div>
            </Card>)}
        </div>

        {filteredMedia.length === 0 && <div className="text-center py-16 text-slate-400" data-id="element-9998">
            <ImageIcon className="w-10 h-10 mx-auto mb-3" data-id="element-9999" />
            <p className="text-sm" data-id="element-10000">Нет файлов в этой категории</p>
          </div>}
      </div>;
  };
  // ==================== REVIEWS ====================
  const renderReviewsManager = () => <div className="space-y-6" data-id="element-10001">
      {backButton('Отзывы')}
      <div className="flex gap-3 -mt-4 mb-2" data-id="element-10002">
        <Badge variant={undefined} size="sm" className="bg-amber-50 text-amber-700 border border-amber-200" data-id="element-10003">
          {cmsReviews.filter(r => r.status === 'pending').length} на модерации
        </Badge>
        <Badge variant={undefined} size="sm" className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-id="element-10004">
          {cmsReviews.filter(r => r.status === 'published').length}{' '}
          опубликовано
        </Badge>
        <Badge variant={undefined} size="sm" className="bg-slate-50 text-slate-600 border border-slate-200" data-id="element-10005">
          {cmsReviews.filter(r => r.status === 'rejected').length} отклонено
        </Badge>
      </div>
      <Card padding="none" className="overflow-hidden" data-id="element-10006">
        <table className="w-full text-left" data-id="element-10007">
          <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase text-slate-500" data-id="element-10008">
            <tr data-id="element-10009">
              <th className="px-4 py-3 font-medium" data-id="element-10010">Автор</th>
              <th className="px-4 py-3 font-medium" data-id="element-10011">Рейтинг</th>
              <th className="px-4 py-3 font-medium" data-id="element-10012">Отзыв</th>
              <th className="px-4 py-3 font-medium" data-id="element-10013">Услуга</th>
              <th className="px-4 py-3 font-medium" data-id="element-10014">Дата</th>
              <th className="px-4 py-3 font-medium" data-id="element-10015">Статус</th>
              <th className="px-4 py-3 text-right font-medium" data-id="element-10016">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-10017">
            {cmsReviews.map(review => <tr key={review.id} className="hover:bg-slate-50 transition-colors" data-id="element-10018">
                <td className="px-4 py-3 text-sm font-medium text-slate-900" data-id="element-10019">
                  {review.author}
                </td>
                <td className="px-4 py-3" data-id="element-10020">{renderStars(review.rating)}</td>
                <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate" data-id="element-10021">
                  {review.text}
                </td>
                <td className="px-4 py-3" data-id="element-10022">
                  <Badge variant="neutral" size="sm" data-id="element-10023">
                    {review.service}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm text-slate-500" data-id="element-10024">
                  {review.date}
                </td>
                <td className="px-4 py-3" data-id="element-10025">
                  <Badge variant={review.status === 'published' ? 'success' : review.status === 'pending' ? 'warning' : 'error'} size="sm" data-id="element-10026">
                    {review.status === 'published' ? 'Опубликован' : review.status === 'pending' ? 'На модерации' : 'Отклонён'}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-right" data-id="element-10027">
                  <div className="flex justify-end gap-1" data-id="element-10028">
                    {review.status === 'pending' && <>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-600" icon={<CheckCircle2 className="w-3.5 h-3.5" data-id="element-10030" />} onClick={() => handleReviewAction(review.id, 'publish')} title="Одобрить" data-id="element-10029" />
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-500" icon={<XCircle className="w-3.5 h-3.5" data-id="element-10032" />} onClick={() => handleReviewAction(review.id, 'reject')} title="Отклонить" data-id="element-10031" />
                      </>}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400" icon={<Trash2 className="w-3.5 h-3.5" data-id="element-10034" />} onClick={() => handleReviewAction(review.id, 'delete')} title="Удалить" data-id="element-10033" />
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </Card>
    </div>;
  // ==================== FAQ EDITOR ====================
  const renderFaqManager = () => <div className="space-y-6" data-id="element-10035">
      <div className="flex items-center justify-between" data-id="element-10036">
        {backButton('FAQ — Часто задаваемые вопросы')}
        <Button variant="primary" size="sm" icon={<Save className="w-4 h-4" data-id="element-10038" />} onClick={() => success('FAQ сохранён')} data-id="element-10037">
          Сохранить всё
        </Button>
      </div>

      {faqCategories.map(category => {
      const items = faqItems.filter(f => f.category === category);
      return <div key={category} data-id="element-10039">
            <div className="flex items-center justify-between mb-3" data-id="element-10040">
              <h3 className="text-base font-bold text-slate-900" data-id="element-10041">
                {category}{' '}
                <span className="text-slate-400 font-normal text-sm" data-id="element-10042">
                  ({items.length})
                </span>
              </h3>
              <Button variant="ghost" size="sm" className="h-7 text-xs" icon={<Plus className="w-3 h-3" data-id="element-10044" />} onClick={() => handleAddFaq(category)} data-id="element-10043">
                Добавить вопрос
              </Button>
            </div>
            <div className="space-y-3 mb-6" data-id="element-10045">
              {items.map(item => <Card key={item.id} padding="sm" className="relative" data-id="element-10046">
                  <div className="absolute top-3 right-3" data-id="element-10047">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-400 hover:text-rose-600" icon={<Trash2 className="w-3.5 h-3.5" data-id="element-10049" />} onClick={() => handleDeleteFaq(item.id)} data-id="element-10048" />
                  </div>
                  <div className="space-y-3 pr-10" data-id="element-10050">
                    <div data-id="element-10051">
                      <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-10052">
                        Вопрос
                      </label>
                      <input className="w-full rounded-lg border-slate-200 shadow-sm px-3 py-2 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all text-sm font-medium" value={item.question} onChange={e => handleFaqChange(item.id, 'question', e.target.value)} placeholder="Введите вопрос..." data-id="element-10053" />
                    </div>
                    <div data-id="element-10054">
                      <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-10055">
                        Ответ
                      </label>
                      <textarea className="w-full rounded-lg border-slate-200 shadow-sm px-3 py-2 border outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 transition-all resize-y min-h-[60px] text-sm" value={item.answer} onChange={e => handleFaqChange(item.id, 'answer', e.target.value)} placeholder="Введите ответ..." data-id="element-10056" />
                    </div>
                  </div>
                </Card>)}
            </div>
          </div>;
    })}

      <button onClick={() => {
      const name = 'Новая категория';
      handleAddFaq(name);
    }} className="w-full flex items-center justify-center gap-2 p-3 rounded-xl border-2 border-dashed border-slate-200 text-slate-500 hover:text-indigo-600 hover:border-indigo-300 hover:bg-indigo-50/50 transition-all font-medium text-sm" data-id="element-10057">
        <Plus className="w-4 h-4" data-id="element-10058" /> Добавить категорию
      </button>
    </div>;
  // ==================== PROMOTIONS ====================
  const renderPromotionsManager = () => <div className="space-y-6" data-id="element-10059">
      <div className="flex items-center justify-between" data-id="element-10060">
        {backButton('Акции и промокоды')}
        <Button variant="primary" size="sm" icon={<Plus className="w-4 h-4" data-id="element-10062" />} onClick={() => setShowPromoForm(true)} data-id="element-10061">
          Создать акцию
        </Button>
      </div>

      {showPromoForm && <Card padding="md" className="border-indigo-200 bg-indigo-50/30" data-id="element-10063">
          <div className="flex items-center justify-between mb-4" data-id="element-10064">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-10065">Новая акция</h3>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" icon={<X className="w-4 h-4" data-id="element-10067" />} onClick={() => setShowPromoForm(false)} data-id="element-10066" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-10068">
            <Input label="Название" value={promoForm.name} onChange={e => setPromoForm(p => ({
          ...p,
          name: e.target.value
        }))} placeholder="Весенняя скидка" data-id="element-10069" />
            <Input label="Промокод" value={promoForm.code} onChange={e => setPromoForm(p => ({
          ...p,
          code: e.target.value
        }))} placeholder="SPRING20" data-id="element-10070" />
            <Input label="Скидка" value={promoForm.discount} onChange={e => setPromoForm(p => ({
          ...p,
          discount: e.target.value
        }))} placeholder="20%" data-id="element-10071" />
            <Input label="Дата начала" value={promoForm.startDate} onChange={e => setPromoForm(p => ({
          ...p,
          startDate: e.target.value
        }))} placeholder="01.04.2026" data-id="element-10072" />
            <Input label="Дата окончания" value={promoForm.endDate} onChange={e => setPromoForm(p => ({
          ...p,
          endDate: e.target.value
        }))} placeholder="30.04.2026" data-id="element-10073" />
            <div className="flex items-end" data-id="element-10074">
              <Button variant="primary" className="w-full" onClick={handleCreatePromo} data-id="element-10075">
                Создать
              </Button>
            </div>
          </div>
        </Card>}

      <div className="space-y-3" data-id="element-10076">
        {promotions.map(promo => <Card key={promo.id} padding="md" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-10077">
            <div className="flex items-center gap-4" data-id="element-10078">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${promo.status === 'active' ? 'bg-emerald-50 text-emerald-600' : promo.status === 'scheduled' ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-400'}`} data-id="element-10079">
                <Tag className="w-5 h-5" data-id="element-10080" />
              </div>
              <div data-id="element-10081">
                <div className="font-medium text-slate-900" data-id="element-10082">{promo.name}</div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-500" data-id="element-10083">
                  <span className="font-mono font-bold text-indigo-600" data-id="element-10084">
                    {promo.code}
                  </span>
                  <span data-id="element-10085">Скидка: {promo.discount}</span>
                  <span data-id="element-10086">
                    {promo.startDate} — {promo.endDate}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-10087">
              <div className="text-right" data-id="element-10088">
                <div className="text-sm font-bold text-slate-900" data-id="element-10089">
                  {promo.usageCount}
                </div>
                <div className="text-xs text-slate-500" data-id="element-10090">использований</div>
              </div>
              <Badge variant={promo.status === 'active' ? 'success' : promo.status === 'scheduled' ? 'warning' : 'neutral'} size="sm" data-id="element-10091">
                {promo.status === 'active' ? 'Активна' : promo.status === 'scheduled' ? 'Запланирована' : 'Истекла'}
              </Badge>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-400 hover:text-rose-600" icon={<Trash2 className="w-4 h-4" data-id="element-10093" />} onClick={() => handleDeletePromo(promo.id)} data-id="element-10092" />
            </div>
          </Card>)}
      </div>

      {promotions.length === 0 && <div className="text-center py-16 text-slate-400" data-id="element-10094">
          <Tag className="w-10 h-10 mx-auto mb-3" data-id="element-10095" />
          <p className="text-sm" data-id="element-10096">Нет активных акций</p>
        </div>}
    </div>;
  // ==================== MAIN RENDER ====================
  return <div className="max-w-6xl mx-auto" data-id="element-10097">
      {currentView === 'dashboard' && renderDashboard()}
      {currentView === 'pages' && renderPagesList()}
      {currentView === 'page_editor' && renderPageEditor()}
      {currentView === 'blog' && renderBlogList()}
      {currentView === 'blog_editor' && renderBlogEditor()}
      {currentView === 'media' && renderMediaLibrary()}
      {currentView === 'reviews' && renderReviewsManager()}
      {currentView === 'faq' && renderFaqManager()}
      {currentView === 'promotions' && renderPromotionsManager()}

      {/* Confirmation modal for category deletion */}
      {categoryToDelete && <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setCategoryToDelete(null)} data-id="element-10098">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-id="element-10099">
            <div className="flex items-center gap-3 mb-4" data-id="element-10100">
              <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center" data-id="element-10101">
                <Trash2 className="w-5 h-5 text-rose-600" data-id="element-10102" />
              </div>
              <h3 className="text-lg font-bold text-slate-900" data-id="element-10103">
                Удалить категорию?
              </h3>
            </div>
            <p className="text-sm text-slate-600 mb-2" data-id="element-10104">
              Вы удаляете категорию{' '}
              <span className="font-semibold text-slate-900" data-id="element-10105">
                «{categoryToDelete}»
              </span>
              .
            </p>
            <p className="text-sm text-slate-500 mb-1" data-id="element-10106">
              {blogPosts.filter(p => p.category === categoryToDelete).length > 0 ? <>
                  В этой категории{' '}
                  <span className="font-semibold text-amber-600" data-id="element-10107">
                    {blogPosts.filter(p => p.category === categoryToDelete).length}
                  </span>{' '}
                  {(() => {
              const count = blogPosts.filter(p => p.category === categoryToDelete).length;
              if (count === 1) return 'статья';
              if (count >= 2 && count <= 4) return 'статьи';
              return 'статей';
            })()}
                  . Они не будут удалены, а станут{' '}
                  <span className="font-semibold" data-id="element-10108">«Без категории»</span>.
                </> : 'В этой категории нет статей.'}
            </p>
            <div className="flex gap-3 mt-6" data-id="element-10109">
              <button onClick={() => setCategoryToDelete(null)} className="flex-1 px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors" data-id="element-10110">
                Отмена
              </button>
              <button onClick={() => performCategoryDeletion(categoryToDelete)} className="flex-1 px-4 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700 transition-colors" data-id="element-10111">
                Удалить
              </button>
            </div>
          </div>
        </div>}
    </div>;
}